'use client';

import { c as _c } from "react/compiler-runtime";
import { type ReactNode, type RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import IconClose from '@/components/assets/icon_close.svg';
import { LOGO_OVERLAY_SCREEN_EDGE_MARGIN } from '@/lib/logo-overlay';
type DrawerCoords = {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
  panelMaxWidth: number;
};
type LogoOverlayRecommendationPanelProps = {
  isOpen: boolean;
  /** 'above': panel expands above the button (bottom-positioned logo).
   *  'below': panel expands below the button (top-positioned logo). */
  placement: 'above' | 'below';
  /** Horizontal alignment derived from logo position. */
  alignment: 'left' | 'right' | 'center';
  anchorRef: RefObject<HTMLElement | null>;
  children: ReactNode;
  /** Whether to show the close button (desktop only; caller controls via UA detection). */
  showCloseButton?: boolean;
  onCloseAction?: () => void;
  /**
   * Ref to the game container element.
   * When provided, the panel will not extend beyond the container's vertical bounds.
   * Pass this when the game viewport is smaller than the browser viewport (e.g. WorldIframe).
   */
  containerRef?: RefObject<HTMLElement | null>;
};
const GAP = 8;
/** Min gap on the button's side; equals LOGO_OVERLAY_SCREEN_EDGE_MARGIN. */
const BUTTON_SIDE_MARGIN = LOGO_OVERLAY_SCREEN_EDGE_MARGIN;
/** Min gap between panel edge and the screen edge on the opposite side (breathing room). */
const OPPOSITE_SIDE_MARGIN = 32;
export default function LogoOverlayRecommendationPanel(t0) {
  const $ = _c(46);
  const {
    isOpen,
    placement,
    alignment,
    anchorRef,
    children,
    showCloseButton: t1,
    onCloseAction,
    containerRef
  } = t0;
  const showCloseButton = t1 === undefined ? false : t1;
  const [mounted, setMounted] = useState(false);
  const [coords, setCoords] = useState(null);
  const [shouldRender, setShouldRender] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const drawerRef = useRef(null);
  const edgeY = placement === "above" ? 8 : -8;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setMounted(true);
    };
    t3 = [];
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  useEffect(t2, t3);
  let t4;
  if ($[2] !== isOpen || $[3] !== shouldRender) {
    t4 = () => {
      if (isOpen) {
        setShouldRender(true);
        setIsExiting(false);
      } else {
        if (shouldRender) {
          setIsExiting(true);
        }
      }
    };
    $[2] = isOpen;
    $[3] = shouldRender;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== isOpen) {
    t5 = [isOpen];
    $[5] = isOpen;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  if ($[7] !== isExiting) {
    t6 = e => {
      if (e.target !== drawerRef.current) {
        return;
      }
      if (isExiting) {
        setShouldRender(false);
        setIsExiting(false);
      }
    };
    $[7] = isExiting;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const handleAnimationEnd = t6;
  let t7;
  if ($[9] !== alignment || $[10] !== anchorRef || $[11] !== containerRef || $[12] !== placement) {
    t7 = () => {
      if (!anchorRef.current) {
        return null;
      }
      const rect = anchorRef.current.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const containerRect = containerRef?.current?.getBoundingClientRect();
      const clampLeft = containerRect?.left ?? 0;
      const clampRight = containerRect?.right ?? vw;
      const vertical = placement === "above" ? {
        bottom: vh - rect.top + GAP
      } : {
        top: rect.bottom + GAP
      };
      let horizontal;
      let panelMaxWidth;
      if (alignment === "right") {
        const right = Math.max(BUTTON_SIDE_MARGIN, vw - rect.right);
        horizontal = {
          right
        };
        panelMaxWidth = Math.max(0, vw - right - clampLeft - OPPOSITE_SIDE_MARGIN);
      } else {
        if (alignment === "center") {
          horizontal = {
            left: clampLeft,
            right: vw - clampRight
          };
          panelMaxWidth = Math.max(0, clampRight - clampLeft - 2 * OPPOSITE_SIDE_MARGIN);
        } else {
          const left = Math.max(BUTTON_SIDE_MARGIN, rect.left);
          horizontal = {
            left
          };
          panelMaxWidth = Math.max(0, clampRight - left - OPPOSITE_SIDE_MARGIN);
        }
      }
      return {
        ...vertical,
        ...horizontal,
        panelMaxWidth
      };
    };
    $[9] = alignment;
    $[10] = anchorRef;
    $[11] = containerRef;
    $[12] = placement;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const computeCoords = t7;
  let t8;
  if ($[14] !== computeCoords) {
    t8 = () => {
      const next = computeCoords();
      if (!next) {
        return;
      }
      setCoords(next);
    };
    $[14] = computeCoords;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const updateCoords = t8;
  let t10;
  let t9;
  if ($[16] !== isOpen || $[17] !== updateCoords) {
    t9 = () => {
      if (!isOpen) {
        return;
      }
      updateCoords();
      window.addEventListener("resize", updateCoords);
      return () => window.removeEventListener("resize", updateCoords);
    };
    t10 = [isOpen, updateCoords];
    $[16] = isOpen;
    $[17] = updateCoords;
    $[18] = t10;
    $[19] = t9;
  } else {
    t10 = $[18];
    t9 = $[19];
  }
  useLayoutEffect(t9, t10);
  if (!mounted || !shouldRender || !coords) {
    return null;
  }
  const portalTarget = document.fullscreenElement as HTMLElement | null ?? document.body;
  const t11 = `${isExiting ? "animate-logo-overlay-drawer-exit" : "animate-logo-overlay-drawer-enter"} ${alignment === "center" ? "flex items-start justify-center" : "w-fit"}`;
  const t12 = `${edgeY}px`;
  let t13;
  if ($[20] !== alignment || $[21] !== coords.panelMaxWidth) {
    t13 = alignment !== "center" && {
      maxWidth: coords.panelMaxWidth
    };
    $[20] = alignment;
    $[21] = coords.panelMaxWidth;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== coords.bottom || $[24] !== coords.left || $[25] !== coords.right || $[26] !== coords.top || $[27] !== t12 || $[28] !== t13) {
    t14 = {
      "--logo-overlay-edge-y": t12,
      position: "fixed",
      top: coords.top,
      bottom: coords.bottom,
      left: coords.left,
      right: coords.right,
      ...t13,
      zIndex: 9999
    };
    $[23] = coords.bottom;
    $[24] = coords.left;
    $[25] = coords.right;
    $[26] = coords.top;
    $[27] = t12;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const t15 = t14 as React.CSSProperties;
  let t16;
  if ($[30] !== coords.panelMaxWidth) {
    t16 = {
      maxWidth: coords.panelMaxWidth
    };
    $[30] = coords.panelMaxWidth;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== children) {
    t17 = <div className="overflow-hidden rounded-2xl bg-black/40">{children}</div>;
    $[32] = children;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== onCloseAction || $[35] !== showCloseButton) {
    t18 = showCloseButton && onCloseAction && <button type="button" onClick={onCloseAction} className="absolute -top-2 -right-2 z-interactive flex h-6 w-6 items-center justify-center rounded-full bg-[rgba(0,0,0,0.4)] backdrop-blur-md disabled:bg-[#2A3649] [&_path]:fill-[#D5E2F2] hover:[&_path]:fill-white active:[&_path]:fill-[#6E7C8E] disabled:[&_path]:fill-[#4B596C]" aria-label="Close recommendations"><IconClose className="h-4 w-4" /></button>;
    $[34] = onCloseAction;
    $[35] = showCloseButton;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== t16 || $[38] !== t17 || $[39] !== t18) {
    t19 = <div className="relative w-fit" style={t16}>{t17}{t18}</div>;
    $[37] = t16;
    $[38] = t17;
    $[39] = t18;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== handleAnimationEnd || $[42] !== t11 || $[43] !== t15 || $[44] !== t19) {
    t20 = createPortal(<div ref={drawerRef} className={t11} style={t15} onAnimationEnd={handleAnimationEnd}>{t19}</div>, portalTarget);
    $[41] = handleAnimationEnd;
    $[42] = t11;
    $[43] = t15;
    $[44] = t19;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  return t20;
}